import React, { useState, ChangeEvent, useRef, useCallback, useEffect } from "react";
import register from "../../registerServiceWorker";
import { getInputClassName } from "../../utils/utils";
import { NumberInputProps } from "../types/NumberInput.types";

function NumberInput(props: NumberInputProps): JSX.Element {
    const [touched, setTouch] = useState(props.touched);
    const [error, setError] = useState("");
    const [htmlClass, setHtmlClass] = useState("");
    const [, setValue] = useState(0);
    const [className, setClassName] = useState(getInputClassName(touched, props.inputClass, htmlClass));

    // Used for referencing the input fields
    const numberInputRef = useRef<HTMLInputElement>();

    const setFocus = useCallback((): void => {
        numberInputRef.current?.focus();
    }, []);

    useEffect(() => {
        // Autofocus the element.
        if (props.autofocus) {
            setTimeout(setFocus, 500);
        }
    }, [props.autofocus, setFocus]);

    useEffect(() => {
        if (!props.touched) {
            setHtmlClass("");
            setClassName(getInputClassName(props.touched, props.inputClass, ""));
        }
    }, [props.touched, props.inputClass]);

    function onValueChanged(event: ChangeEvent<HTMLInputElement>): void {
        let elementValue: number = (isNaN(Number(event.target.value))) ? 0 : Number(event.target.value);
        let [error, validClass] = ["", ""];

        if (!error) {
            [error, validClass] = ((props.max != null) && elementValue > (props.max)) ?
            [`Value can't be higher than ${props.max} `, "is-invalid"] : ["", "is-valid"];
        }

        if (!error) {
            [error, validClass] = ((props.min != null) && elementValue < (props.min)) ?
            [`Value can't be lower than ${props.min} `, "is-invalid"] : ["", "is-valid"];
        }

        props.onChange({ value: elementValue, error: error, touched: true, field: props.field });

        setTouch(true);
        setError(error);
        setHtmlClass(validClass);
        setValue(elementValue);
        setClassName(getInputClassName(true, props.inputClass, validClass));
    }

    /**
     * Selects the content of the number field when the input field gets focus.
     * @param event
     */
    function onFocus(event: React.FocusEvent<HTMLInputElement>): void {
        event.target.select();
    }

    return (
        <div>
            <label htmlFor={props.id.toString()}>{props.label}{props.required ? <small> (required)</small> : ""}</label>
            <input
                value={props.value}
                type="number"
                onChange={onValueChanged}
                className={className}
                id={`id_${props.label}`}
                ref={(e: HTMLInputElement) => { register(); numberInputRef.current = e; }}
                onFocus={onFocus}
            />
            {error ?
                <div className="invalid-feedback">
                    {error}
                </div> : null
            }
        </div>
    );
}

export default NumberInput;